// crewbit-admin/src/components/money/NeedsReviewBanner.tsx
//
// Page-level amber banner above ExpensesTable. Visibility gated on count > 0
// (calm-empty discipline — banner hidden when nothing needs review).
// Click flips ExpenseFilters.needsReviewOnly to filter the table to
// uncategorized + low-confidence AI-categorized rows.
//
// Spec: docs/references/MONEY_MODULE_SPEC_v1.md §Expenses.D3 (needs-review).

import { AlertTriangle, X } from 'lucide-react';
import { palette, admin, radii, spacing, transition } from '../../styles/tokens';

interface NeedsReviewBannerProps {
  count: number;
  active: boolean;
  onActivate: () => void;
  onClear: () => void;
}

export function NeedsReviewBanner({ count, active, onActivate, onClear }: NeedsReviewBannerProps) {
  if (count === 0) return null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.md,
        padding: `${spacing.sm + 2}px ${spacing.md}px`,
        background: palette.amberWash,
        border: `1px solid ${palette.amber}`,
        borderRadius: admin.cardRadius,
        marginBottom: spacing.md,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: radii.sm,
          background: palette.amber,
          color: palette.white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <AlertTriangle size={15} strokeWidth={2.25} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: palette.amber }}>
          {count} {count === 1 ? 'expense needs' : 'expenses need'} your review
        </div>
        <div style={{ fontSize: 12, color: palette.amber, marginTop: 2 }}>
          {active
            ? 'Showing only flagged expenses. Clear to see the full list.'
            : 'Missing categories or low-confidence AI suggestions. Tap to filter.'}
        </div>
      </div>
      {active ? (
        <button
          type="button"
          onClick={onClear}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '6px 12px',
            borderRadius: radii.sm,
            border: `1px solid ${palette.amber}`,
            background: 'transparent',
            color: palette.amber,
            fontSize: 12.5,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
            transition: `background ${transition.fast}`,
          }}
        >
          <X size={12} strokeWidth={2.5} />
          Clear filter
        </button>
      ) : (
        <button
          type="button"
          onClick={onActivate}
          style={{
            padding: '6px 14px',
            borderRadius: radii.sm,
            border: 'none',
            background: palette.amber,
            color: palette.white,
            fontSize: 12.5,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Review now
        </button>
      )}
    </div>
  );
}
